import { defineComponent as _defineComponent } from 'vue'
import useResponsiveness from '@/mixins/useResponsiveness';
import {
  computed, nextTick, PropType, ref, watch
} from 'vue';
import {
  onClickOutside,
  templateRef, toRefs, useDraggable, useMouse, useVModels
} from '@vueuse/core';
import useAdaptBounding from '@/mixins/useAdaptBounding';
import onSizeChange from '@/mixins/onSizeChange';
import useScrollLock from '@/mixins/useScrollLock';
import usePageState from '@/state/pageState';
import type { CSSProperties } from 'vue';

type ResizeDirection =
  | 'top'
  | 'right'
  | 'bottom'
  | 'left'
  | 'top-right'
  | 'bottom-right'
  | 'bottom-left'
  | 'top-left'


export default /*#__PURE__*/_defineComponent({
  __name: 'BCXQuickLayer',
  props: {
  xPos: {
    type: Number,
    default: 0
  },
  yPos: {
    type: Number,
    default: 0
  },
  isOpen: Boolean,
  adaptBoundingAfterMove: Boolean,
  adaptBoundingOnSizeChange: Boolean,
  width: {
    type: [Number, String] as PropType<number | 'auto'>,
    default: 'auto'
  },
  minWidthPx: {
    type: Number,
    default: 20
  },
  height: {
    type: [Number, String] as PropType<number | 'auto'>,
    default: 'auto'
  },
  minHeightPx: {
    type: Number,
    default: 20
  },
  autoPosition: Boolean,
  autoPositionHAlign: { type: String as PropType<'left' | 'center' | 'right'>, default: 'center' },
  autoPositionVAlign: { type: String as PropType<'top' | 'center' | 'bottom'>, default: 'center' },
  mobileFullscreen: Boolean,
  fullscreen: Boolean,
  scrollLock: Boolean,
  addZIndex: {
    type: Number,
    default: 0
  },
  // remember to use is-open.sync if you use this one:
  closeOnClickOutside: Boolean,
  // hasTip: Boolean,

  resizable: {
    type: Boolean,
    default: false
  },

  movable: {
    type: Boolean,
    default: true
  },

  layerStyle: {
    type: Object as PropType<CSSProperties>,
    default: () => ({})
  }
},
  emits: ['dragend', 'has-dissapeared', 'update:is-open'],
  setup(__props, { expose, emit }) {

const props = __props;





const {
  adaptBoundingAfterMove, isOpen, autoPosition, adaptBoundingOnSizeChange, mobileFullscreen, autoPositionHAlign, autoPositionVAlign, fullscreen
} = toRefs(props);
const { xPos, yPos } = useVModels(props);
const { isMobileWidth } = useResponsiveness();
const layer = templateRef<HTMLElement>('layer');
const el = templateRef<HTMLElement>('el');
const isTransitioning = ref(false);

const { x: xMouse, y: yMouse } = useMouse({ type: 'client' });
const { lastMouseDownEvent } = usePageState();

const x = ref(xPos.value);
const y = ref(yPos.value);

const height = ref(props.height);
const width = ref(props.width);

/**
 * Checks if QuickLayer is resizable and the target is a resize handle.
 * Used to prevent dragging if user is intending to resize the QuickLayer.
 * @param event - The mouse event.
 * @param target - The target element.
 * @returns True if QuickLayer is resizable and the target is a resize handle, false otherwise.
 */
const isResizing = (event: MouseEvent, target: HTMLElement) => {
  if (!props.resizable) return false;

  const targetIsResizeHandle = target.closest('.bc-quick-layer--resize-handle') !== null;

  return targetIsResizeHandle;
};

const isMovableTarget = (event: MouseEvent | PointerEvent) => {
  const target = event.target as HTMLElement;

  if (isResizing(event, target)) return false;

  if (target.closest('.no-move')) return false;

  if (['TEXTAREA', 'INPUT', 'SELECT', 'BUTTON'].includes(target.nodeName)) return false;

  const hasVertScrollbar = target.scrollHeight > target.clientHeight;
  const rect = target.getBoundingClientRect();
  if (hasVertScrollbar && (event.clientX - rect.left) > target.clientWidth - 12) return false;

  // is it inside THIS quicklayer? If not, return false and don't drag.
  // This allows nested quicklayers to function properly ;-)
  const closestQuickLayer = target.closest('.bc-quick-layer-holder');
  const isOnSame = closestQuickLayer === el.value;
  if (!isOnSame) return false;

  return true;
};

if (props.movable) {
  const { x: dragX, y: dragY } = useDraggable(layer, {
    initialValue: {
      x: xPos.value,
      y: yPos.value
    },
    // eslint-disable-next-line consistent-return
    onStart(_, event) {
      if (!isMovableTarget(event)) return false;

      event.preventDefault();
    },
    async onEnd() {
      if (adaptBoundingAfterMove.value) {
        await nextTick();
        // eslint-disable-next-line no-use-before-define
        adaptBounding(0.75);
      }
      xPos.value = x.value;
      yPos.value = y.value;
      emit('dragend', { x: x.value, y: y.value });
    }
  });

  watch(dragX, (val) => { x.value = val; });
  watch(dragY, (val) => { y.value = val; });
}

const { adaptBounding: adapt } = useAdaptBounding(layer, x, y, 4);

watch([xPos, yPos], ([xPos, yPos]) => {
  x.value = xPos;
  y.value = yPos;
});

watch(() => props.width, (newWidth) => {
  if (newWidth !== undefined && newWidth !== width.value) {
    width.value = newWidth;
  }
});

watch(() => props.height, (newHeight) => {
  if (newHeight !== undefined && newHeight !== height.value) {
    height.value = newHeight;
  }
});

// Save position before going fullscreen and restore it when exiting
const xPosBeforeFullscreen = ref(x.value);
const yPosBeforeFullscreen = ref(y.value);

watch(fullscreen, (isFullscreen) => {
  if (isFullscreen) {
    xPosBeforeFullscreen.value = x.value;
    yPosBeforeFullscreen.value = y.value;
  } else {
    x.value = xPosBeforeFullscreen.value;
    y.value = yPosBeforeFullscreen.value;
    // Update parent props via useVModels
    xPos.value = x.value;
    yPos.value = y.value;
  }
});

const style = computed(() => (isMobileWidth.value || fullscreen.value
  ? props.layerStyle
  : {
    left: `${x.value}px`,
    top: `${y.value}px`,
    height: height.value === 'auto' ? 'auto' : `${height.value}px`,
    touchAction: props.movable ? 'none' : 'auto',
    ...props.layerStyle
  }));

function adaptBounding(outsideRatio = 0) {
  if (!isMobileWidth.value) {
    adapt(outsideRatio);
  }
  if (isTransitioning.value) requestAnimationFrame(() => adaptBounding(outsideRatio));
}

watch(isOpen, (is) => {
  if (is && autoPosition.value) {
    if (autoPositionVAlign.value || autoPositionHAlign.value) {
      const target = lastMouseDownEvent.value?.target as HTMLElement;
      if (!target) return;
      const {
        top, left, bottom, width, height, right
      } = target.getBoundingClientRect();
      let newX = 0; let newY = 0;
      switch (autoPositionVAlign.value) {
        case 'bottom': newY = bottom; break;
        case 'center': newY = top + height * 0.5; break;
        case 'top': newY = top; break;
      }
      switch (autoPositionHAlign.value) {
        case 'left': newX = left; break;
        case 'center': newX = left + width * 0.5; break;
        case 'right': newX = right; break;
      }
      x.value = newX + xPos.value;
      y.value = newY + yPos.value;
    } else {
      x.value = xMouse.value + xPos.value;
      y.value = yMouse.value + yPos.value;
    }
  }
});

const onTransition = (is: boolean, isAppearing: boolean) => {
  isTransitioning.value = is;
  if (!isMobileWidth.value) {
    if (is) {
      adaptBounding(autoPosition.value ? 0 : 0.75);
    }
  }
  if (!is && !isAppearing) {
    emit('has-dissapeared');
  }
};

if (adaptBoundingOnSizeChange.value) {
  onSizeChange(layer, ({ height, oldHeight }) => {
    if (height > oldHeight) adaptBounding(0);
  });
}

if (mobileFullscreen.value) {
  useScrollLock(el, isOpen);
  // usePreventScroll(el);
}

onClickOutside(el, () => {
  if (props.closeOnClickOutside) emit('update:is-open', false);
});

const handleSize = 10;

const cursorStyle = (direction: 'ns' | 'ne' | 'ew' | 'se' | 'sw' | 'nw') => {
  if (props.resizable) {
    return `${direction}-resize`;
  }
  if (props.movable) {
    return 'move';
  }
  return 'default';
};

const resizeHandles: Record<ResizeDirection, Record<string, string>> = {
  top: {
    top: '0',
    left: `${handleSize}px`,
    right: `${handleSize}px`,
    height: `${handleSize}px`,
    cursor: cursorStyle('ns'),
  },
  'top-right': {
    top: '0',
    right: '0',
    width: `${handleSize}px`,
    height: `${handleSize}px`,
    cursor: cursorStyle('ne'),
  },
  right: {
    right: '0',
    top: `${handleSize}px`,
    bottom: `${handleSize}px`,
    width: `${handleSize}px`,
    cursor: cursorStyle('ew'),
  },
  'bottom-right': {
    bottom: '0',
    right: '0',
    width: `${handleSize}px`,
    height: `${handleSize}px`,
    cursor: cursorStyle('se'),
  },
  bottom: {
    bottom: '0',
    left: `${handleSize}px`,
    right: `${handleSize}px`,
    height: `${handleSize}px`,
    cursor: cursorStyle('ns'),
  },
  'bottom-left': {
    bottom: '0',
    left: '0',
    width: `${handleSize}px`,
    height: `${handleSize}px`,
    cursor: cursorStyle('sw'),
  },
  left: {
    left: '0',
    top: `${handleSize}px`,
    bottom: `${handleSize}px`,
    width: `${handleSize}px`,
    cursor: cursorStyle('ew'),
  },
  'top-left': {
    top: '0',
    left: '0',
    width: `${handleSize}px`,
    height: `${handleSize}px`,
    cursor: cursorStyle('nw'),
  },
};

const onResizeStart = (event: MouseEvent | TouchEvent, direction: ResizeDirection) => {
  event.preventDefault();
  event.stopPropagation();

  const startingMousePosition = {
    x: 'touches' in event ? event.touches[0].clientX : event.clientX,
    y: 'touches' in event ? event.touches[0].clientY : event.clientY
  };

  const startingLayerPosition = {
    x: x.value,
    y: y.value
  };

  const startingLayerSize = {
    width: layer.value?.clientWidth,
    height: layer.value?.clientHeight
  };

  const applyMinConstraint = (value: number, min: number) => Math.max(value, min);

  const resizeHorizontal = (deltaX: number, side: 'left' | 'right') => {
    if (side === 'right') {
      width.value = applyMinConstraint(startingLayerSize.width + deltaX, props.minWidthPx);
    } else if (side === 'left') {
      const newWidth = startingLayerSize.width - deltaX;
      width.value = applyMinConstraint(newWidth, props.minWidthPx);
      if (newWidth >= props.minWidthPx) {
        x.value = startingLayerPosition.x + deltaX;
      }
    }
  };

  const resizeVertical = (deltaY: number, side: 'top' | 'bottom') => {
    if (side === 'bottom') {
      height.value = applyMinConstraint(startingLayerSize.height + deltaY, props.minHeightPx);
    } else if (side === 'top') {
      const newHeight = startingLayerSize.height - deltaY;
      height.value = applyMinConstraint(newHeight, props.minHeightPx);
      if (newHeight >= props.minHeightPx) {
        y.value = startingLayerPosition.y + deltaY;
      }
    }
  };

  const onMove = (event: MouseEvent | TouchEvent) => {
    if ('touches' in event) {
      event.preventDefault();
    }
    const clientX = 'touches' in event ? event.touches[0].clientX : event.clientX;
    const clientY = 'touches' in event ? event.touches[0].clientY : event.clientY;

    const deltaX = clientX - startingMousePosition.x;
    const deltaY = clientY - startingMousePosition.y;

    if (direction.includes('left')) {
      resizeHorizontal(deltaX, 'left');
    }
    if (direction.includes('right')) {
      resizeHorizontal(deltaX, 'right');
    }
    if (direction.includes('top')) {
      resizeVertical(deltaY, 'top');
    }
    if (direction.includes('bottom')) {
      resizeVertical(deltaY, 'bottom');
    }
  };

  const onEnd = () => {
    document.removeEventListener('mousemove', onMove);
    document.removeEventListener('mouseup', onEnd);
    document.removeEventListener('touchmove', onMove);
    document.removeEventListener('touchend', onEnd);
  };

  document.addEventListener('mousemove', onMove);
  document.addEventListener('mouseup', onEnd);
  document.addEventListener('touchmove', onMove, { passive: false });
  document.addEventListener('touchend', onEnd);
};

// We expose here instead of emitting events because we need the values only once, namely when the parent component unmounts.
// At that point we save the values so we can restore them when the next popup is opened. Emitting events, while probably the more
// idiomatic pattern, would result in more code and constant updates of the values.
//
// If you ever need to react to changes in real-time (e.g., save the position or size while the user is dragging/resizing),
// you could replace this pattern with emits. For example, you could emit 'update:width', 'update:height', etc. on value changes
// or emit a single event with all values at once. This would make the parent component receive reactive updates immediately,
// but would also introduce more boilerplate and potentially unnecessary reactivity for our current use case.
//
// For now, defineExpose gives us a simple and centralized way to read all the final values when the parent unmounts.
expose({
  width: computed(() => width.value),
  height: computed(() => height.value),
  x: computed(() => x.value),
  y: computed(() => y.value),
});


return { __sfc: true,props, emit, adaptBoundingAfterMove, isOpen, autoPosition, adaptBoundingOnSizeChange, mobileFullscreen, autoPositionHAlign, autoPositionVAlign, fullscreen, xPos, yPos, isMobileWidth, layer, el, isTransitioning, xMouse, yMouse, lastMouseDownEvent, x, y, height, width, isResizing, isMovableTarget, adapt, xPosBeforeFullscreen, yPosBeforeFullscreen, style, adaptBounding, onTransition, handleSize, cursorStyle, resizeHandles, onResizeStart }
}

})